import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { showNotification } from 'store/notification.slice';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
// no extra theme wrapper; use app theme directly
import { useEffect, useState } from 'react';
import { useAppDispatch } from 'store/hook';
import type { Asset } from 'types/asset.types';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';

export const TirePosition = ({
  title,
  open,
  handleAbort,
  enableDarkTheme,
  selectedAsset,
  tireData,
  updateTirePosition,
  makeModelDetails,
  tirePatterns,
  customerId,
}: {
  title: string;
  open: boolean;
  handleAbort: any;
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  tireData: any;
  updateTirePosition: any;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const dispatch = useAppDispatch();

  const [rowData, setRowData] = useState<any[]>([]);
  const [originalRowData, setOriginalRowData] = useState<any[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    changes: Array<{ from: any; to: any }>;
  }>({
    open: false,
    changes: [],
  });

  const assetTireDetails = getDynamicAssetTireInfo(
    makeModelDetails,
    tirePatterns.tirePatternPositions,
    selectedAsset.make,
    selectedAsset.model,
    customerId
  );

  const handleSaveAll = () => {
    const changes = getChanges();
    if (changes.length > 0) {
      setConfirmationDialog({
        open: true,
        changes,
      });
    }
  };

  const getChanges = () => {
    const changes: Array<{ from: any; to: any }> = [];

    rowData.forEach((currentRow) => {
      const originalRow = originalRowData.find(
        (orig) => orig.id === currentRow.id
      );
      if (
        originalRow &&
        originalRow.currentPosition !== currentRow.currentPosition
      ) {
        changes.push({
          from: originalRow,
          to: currentRow,
        });
      }
    });

    return changes;
  };

  const handleConfirmSave = async () => {
    try {
      const changes = confirmationDialog.changes;
      const currentTime = convertUtcToDisplayTime(
        new Date(),
        preferredTimeDisplayMode,
        timezone,
        'DD MMM YYYY hh:mm A'
      );
      const datePart = dayjs(currentTime).format('YYYY-MM-DD');
      const fullString = `${datePart} 00:00:00`;
      const result = dayjs.tz(fullString, 'YYYY-MM-DD HH:mm:ss', timezone);
      const utcISOString = result.toISOString();
      const tireMap = assetTireDetails.tireCodes;
      const payload = changes.map((change) => {
        const valueToFind = change.to.currentPosition;
        const matchingKey = Object.entries(tireMap).find(
          ([key, value]) => value === valueToFind
        )?.[0];
        return {
          tireCode: change.to.id,
          oldPosition: change.from.currentPosition,
          newPosition: change.to.currentPosition,
          changeType: 'tire-position-change',
          sensorId: change.to.sensorId,
          newTireCode: matchingKey,
          installedAt: utcISOString,
        };
      });

      updateTirePosition(payload);

      dispatch(
        showNotification({
          message: `${changes.length} tire position(s) updated!`,
          type: 'success',
        })
      );

      setOriginalRowData([...rowData]);
      setHasChanges(false);
    } catch (e) {
      dispatch(
        showNotification({
          message: t('error_while_updating_tire_positions'),
          type: 'error',
        })
      );
    }

    setConfirmationDialog({
      open: false,
      changes: [],
    });
  };

  const handleCancelConfirmation = () => {
    setConfirmationDialog({
      open: false,
      changes: [],
    });
  };

  const handleResetChanges = () => {
    setRowData([...originalRowData]);
    setHasChanges(false);
  };

  const handlePositionChange = (tireId: string, newPosition: string) => {
    setRowData((prevData) => {
      const changingTire = prevData.find((row) => row.id === tireId);
      const displacedTire = prevData.find(
        (row) => row.currentPosition === newPosition && row.id !== tireId
      );

      if (!changingTire) return prevData;

      const oldPosition = changingTire.currentPosition;

      return prevData.map((row) => {
        if (row.id === tireId) {
          return { ...row, currentPosition: newPosition };
        } else if (displacedTire && row.id === displacedTire.id) {
          return { ...row, currentPosition: oldPosition };
        }
        return row;
      });
    });
  };

  useEffect(() => {
    const hasAnyChanges = rowData.some((row) => {
      const original = originalRowData.find((orig) => orig.id === row.id);
      return original && original.currentPosition !== row.currentPosition;
    });
    setHasChanges(hasAnyChanges);
  }, [rowData, originalRowData]);

  // theme taken from clientThemePalette to match AxleWise modal
  const palette =
    clientThemePalette[CLIENT_THEME][enableDarkTheme ? 'dark' : 'light'];
  const modalBgColor = palette.boxBackground;
  const modalTextColor = palette.textPrimary;
  const modalTitleBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.topBar;
  const modalBorderColor = palette.borderGrey || palette.border;
  const accentColor = palette.primary;
  let isSensorIdInitialized = true;

  useEffect(() => {
    if (selectedAsset?.device?.deviceId) {
      const assetTireData = tireData[selectedAsset?.device?.deviceId];
      const fitmentData = assetTireData.tireFitmentData ?? [];
      const assetTireNames = assetTireDetails.tireCodes;
      isSensorIdInitialized = fitmentData.every(
        (item: any) =>
          typeof item.sensorId === 'string' && item.sensorId.trim() !== ''
      );
      const apiTires = fitmentData.map((item: any) => item.tireCode);
      const displayData = apiTires.map((tireCode: any) => {
        const tireFitment = fitmentData.find(
          (item: any) => item.tireCode === tireCode
        );
        return {
          id: tireCode,
          tire: assetTireNames[tireCode] ?? tireCode,
          currentPosition: assetTireNames[tireCode] ?? tireCode,
          installedAt: tireFitment?.installedAt
            ? convertUtcToDisplayTime(
                tireFitment?.installedAt,
                preferredTimeDisplayMode,
                timezone,
                'DD MMM YYYY hh:mm A'
              )
            : null,
          sensorId: tireFitment?.sensorId ?? null,
        };
      });

      setRowData(displayData);
      setOriginalRowData(displayData);
      setHasChanges(false);
    }
  }, [selectedAsset]);

  const availablePositions = [
    ...new Set(originalRowData.map((row) => row.currentPosition)),
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleAbort}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: modalBgColor,
            color: modalTextColor,
            borderRadius: '12px',
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
          }}
        >
          {title}
          <IconButton
            aria-label="close"
            onClick={handleAbort}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '0px', minHeight: '400px' }}>
          {isSensorIdInitialized ? (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  height: 'calc(100vh - 330px)',
                  maxHeight: '400px',
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: modalTitleBgColor,
                          color: modalTextColor,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          borderBottom: `1px solid ${modalBorderColor}`,
                          width: '50%',
                        }}
                      >
                        {t('tire_position')}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: modalTitleBgColor,
                          color: modalTextColor,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          borderBottom: `1px solid ${modalBorderColor}`,
                          width: '50%',
                        }}
                      >
                        {t('new_position')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowData.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: (t) =>
                              t.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.02)'
                                : 'rgba(0,0,0,0.02)',
                          },
                          '&:hover': {
                            backgroundColor: (t) =>
                              t.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(0,0,0,0.04)',
                          },
                          height: '45px',
                        }}
                      >
                        <TableCell
                          sx={{
                            color: modalTextColor,
                            borderBottom: `1px solid ${modalBorderColor}`,
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                          }}
                        >
                          {row.tire}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: modalTextColor,
                            borderBottom: `1px solid ${modalBorderColor}`,
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                              value={row.currentPosition}
                              onChange={(e) => {
                                handlePositionChange(row.id, e.target.value);
                              }}
                              sx={{
                                color: modalTextColor,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: modalBorderColor,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: modalTextColor,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  () => ({
                                    borderColor: accentColor,
                                  }),
                                '& .MuiSvgIcon-root': {
                                  color: 'inherit',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    backgroundColor: modalBgColor,
                                    border: `1px solid ${modalBorderColor}`,
                                    '& .MuiMenuItem-root': {
                                      color: 'inherit',
                                      '&:hover': {
                                        backgroundColor: enableDarkTheme
                                          ? 'rgba(255,255,255,0.08)'
                                          : 'rgba(0,0,0,0.04)',
                                      },
                                      '&.Mui-selected': () => ({
                                        backgroundColor: alpha(
                                          accentColor,
                                          enableDarkTheme ? 0.12 : 0.08
                                        ),
                                        '&:hover': {
                                          backgroundColor: alpha(
                                            accentColor,
                                            enableDarkTheme ? 0.16 : 0.12
                                          ),
                                        },
                                      }),
                                    },
                                  },
                                },
                              }}
                            >
                              {availablePositions.map((position) => (
                                <MenuItem key={position} value={position}>
                                  {position}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  padding: '16px 24px',
                  borderTop: `1px solid ${modalBorderColor}`,
                  backgroundColor: modalTitleBgColor,
                }}
              >
                <Button
                  onClick={handleResetChanges}
                  variant="outlined"
                  color="error"
                  disabled={!hasChanges}
                  sx={{ minWidth: '100px' }}
                >
                  {t('reset_changes')}
                </Button>
                <Button
                  onClick={handleSaveAll}
                  variant="contained"
                  color="success"
                  disabled={!hasChanges}
                  sx={{ minWidth: '100px' }}
                >
                  {t('save_all_changes')}
                </Button>
              </Box>
            </>
          ) : (
            <Typography
              sx={{
                color: modalTextColor,
                fontSize: '14px',
                padding: '10px',
              }}
            >
              {t(
                'tire_position_cannot_be_updated_because_the_sensor_id_is_not'
              )}
              <br />
              {t(
                'please_go_to_the_tpms_sensor_tab_in_the_settings_panel_on_th'
              )}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmationDialog.open}
        onClose={handleCancelConfirmation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: modalBgColor,
            color: modalTextColor,
            borderRadius: '12px',
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
          }}
        >
          {t('confirm_position_changes')}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ py: 2 }}>
            {t('you_are_about_to_make_the_following_position_changes')}
            <br />
            <br />
            {confirmationDialog.changes.map((change, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <strong>{change.to.tire}</strong>: {change.from.currentPosition}{' '}
                {t('x2192')} {change.to.currentPosition}
              </Box>
            ))}
            <br />
            {t('are_you_sure_you_want_to_proceed')}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 24px', gap: 1 }}>
          <Button
            onClick={handleCancelConfirmation}
            variant="outlined"
            color="error"
            sx={{ minWidth: '80px' }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            color="success"
            sx={{ minWidth: '80px' }}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
